import { useState } from "react";
import { Button } from "@/components/ui/button";
import { siteEquipmentApi, type EquipmentArticle } from "@/lib/api/oprema";
import { procitajGreske } from "@/lib/greske";
import { Field, SaveErrors, useEquipmentAccess } from "./shared";

export function ArticleQuickCreate({
  empty,
  open,
  onOpenChange,
  onCreated,
}: {
  empty: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (article: EquipmentArticle) => void;
}) {
  const access = useEquipmentAccess();
  const [name, setName] = useState("");
  const [group, setGroup] = useState<EquipmentArticle["skupina"]>("skela");
  const [mode, setMode] = useState<EquipmentArticle["nacin"]>("kolicina");
  const [price, setPrice] = useState("");
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const priceValid = !price || /^\d{1,10}([.,]\d{1,2})?$/.test(price);
  const valid = !!name.trim() && priceValid;
  async function save() {
    if (!valid || pending || !access.catalogue) return;
    setPending(true);
    setErrors({});
    setError("");
    try {
      const article = await siteEquipmentApi.saveArticle({
        naziv: name.trim(),
        skupina: group,
        nacin: mode,
        jedinica: "kom",
        m2_po_kom: null,
        nabavna_cijena: price ? price.replace(",", ".") : null,
        aktivna: true,
      });
      onCreated(article);
      onOpenChange(false);
      setName("");
      setPrice("");
    } catch (cause) {
      const parsed = procitajGreske(cause);
      setErrors(parsed.polja);
      setError(parsed.opcenito.join(" "));
    } finally {
      setPending(false);
    }
  }
  return (
    <>
      {empty && <p>Još nema artikala</p>}
      {!access.catalogue ? (
        empty && <p>Javi skladištaru</p>
      ) : !open ? (
        <Button type="button" variant="outline" onClick={() => onOpenChange(true)}>
          Novi artikl
        </Button>
      ) : (
        <fieldset
          className="equipment-card equipment-form"
          disabled={pending}
          onKeyDown={(event) => {
            if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
              event.preventDefault();
              void save();
            }
          }}
        >
          <legend>Novi artikl</legend>
          <Field label="Naziv" error={errors.naziv}>
            <input
              value={name}
              maxLength={200}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="Skupina" error={errors.skupina}>
            <select
              value={group}
              onChange={(event) => setGroup(event.target.value as EquipmentArticle["skupina"])}
            >
              <option value="skela">Skela</option>
              <option value="oplata">Oplata</option>
              <option value="podupiraci">Podupirači</option>
              <option value="alat">Alat</option>
              <option value="ostalo">Ostalo</option>
            </select>
          </Field>
          <Field label="Način" error={errors.nacin}>
            <select
              value={mode}
              onChange={(event) => setMode(event.target.value as EquipmentArticle["nacin"])}
            >
              <option value="kolicina">Po količini</option>
              <option value="komad">Po komadu</option>
            </select>
          </Field>
          <Field label="Nabavna cijena (neobavezno)" error={errors.nabavna_cijena}>
            <input
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
          </Field>
          {!priceValid && <p>Upiši nenegativnu cijenu s najviše dvije decimale.</p>}
          <SaveErrors
            error={error}
            errors={errors}
            inline={["naziv", "skupina", "nacin", "nabavna_cijena"]}
          />
          <Button type="button" disabled={pending || !valid} onClick={() => void save()}>
            Spremi artikl
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            Odustani od artikla
          </Button>
        </fieldset>
      )}
    </>
  );
}
